<template>
    <v-dialog
        v-if="report && Object.keys(report).length > 0"
        v-model="show"
        max-width="500px"
        persistent
        @keydown.esc="closeDialog"
    >
        <v-snackbar
            v-model="showAlert"
            color="red lighten-1"
            text
            top
            right
            rounded
            class="mt-3"
        >
            {{ alertMessage }}
            <template v-slot:action="{ attrs }">
                <v-btn
                    color="red lighten-1"
                    text
                    v-bind="attrs"
                    @click="showToast = false"
                >
                    <v-icon color="red lighten-1">
                        close
                    </v-icon>
                </v-btn>
            </template>
        </v-snackbar>
        <v-card>
            <v-card-title class="text-h6">
                Generate Report
            </v-card-title>
            <v-divider />
            <v-card-text
                v-if="!reportGenerateStatus"
                class="mt-4"
            >
                <h3>{{ report.title }}</h3>
                <span class="">{{ report.description }}</span>
                <v-form
                    ref="form"
                    v-model="valid"
                    lazy-validation
                    class="mt-4"
                >
                    <!-- center-->
                    <v-select
                        v-if="report.param_csv.includes('center')"
                        v-model="center"
                        :items="companyCenters"
                        item-text="text"
                        item-value="value"
                        outlined
                        label="811 Center"
                    />
                    <!-- 'ticket'-->
                    <v-text-field
                        v-if="report.param_csv.includes('ticket')"
                        ref="ticketNumber"
                        v-model="ticket"
                        outlined
                        type='text'
                        label="Ticket Number"
                        prepend-inner-icon="receipt"
                        :error="missingTicketNumber"
                    />
                    <!-- 'date_field'-->
                    <v-select
                        v-if="report.param_csv.includes('date_field')"
                        v-model="date_field"
                        :items="dateFields"
                        item-text="title"
                        item-value="value"
                        outlined
                        label="Date Field"
                    />
                    <!-- 'accounts'-->
                    <v-combobox
                        v-if="report.param_csv.includes('accounts')"
                        v-model="accounts"
                        :items="companyUsers"
                        item-text="text"
                        item-value="value"
                        outlined
                        multiple
                        small-chips
                        :label="accounts.length === 0 ? 'All Users' : 'User(s)'"
                        @blur="addCustomAccount"
                    />
                    <!-- 'member_codes'-->
                    <v-combobox
                        v-if="report.param_csv.includes('member_codes')"
                        v-model="member_codes"
                        :items="companyMemberCodes"
                        item-text="text"
                        item-value="value"
                        outlined
                        multiple
                        small-chips
                        :label="member_codes.length === 0 ? 'All Members' : 'Member Code(s)'"
                    />
                    <!-- 'response_codes'-->
                    <v-combobox
                        v-if="report.param_csv.includes('response_codes')"
                        v-model="response_codes"
                        :items="companyResponseCodes"
                        item-text="text"
                        item-value="value"
                        outlined
                        multiple
                        small-chips
                        :label="response_codes.length === 0 ? 'All Response Codes' : 'Response Code(s)'"
                    />
                    <!-- 'billing_codes'-->
                    <v-combobox
                        v-if="report.param_csv.includes('billing_codes')"
                        v-model="billing_codes"
                        :items="companyBillingCodes"
                        item-text="text"
                        item-value="value"
                        outlined
                        multiple
                        small-chips
                        :label="billing_codes.length === 0 ? 'All Billing Codes' : 'Billing Code(s)'"
                    />
                    <!-- 'locators'-->
                    <v-combobox
                        v-if="report.param_csv.includes('locators')"
                        v-model="locators"
                        :items="companyLocators"
                        item-text="text"
                        item-value="value"
                        outlined
                        multiple
                        small-chips
                        :label="locators.length === 0 ? 'All Locators' : 'Locator(s)'"
                    />
                    <!-- 'date_from' and 'date_to'-->
                    <v-menu
                        v-if="report.param_csv.includes('date_from') && report.param_csv.includes('date_to')"
                        ref="dateMenu"
                        v-model="dateMenu"
                        :close-on-content-click="false"
                        :return-value.sync="date"
                        transition="scale-transition"
                        offset-y
                        min-width="auto"
                    >
                        <template v-slot:activator="{ on, attrs }">
                            <v-text-field
                                v-model="dateText"
                                label="Date Range*"
                                append-icon="mdi-calendar"
                                readonly
                                v-bind="attrs"
                                outlined
                                v-on="on"
                            />
                        </template>
                        <v-date-picker
                            v-model="date"
                            no-title
                            range
                            scrollable
                        >
                            <v-spacer />
                            <v-btn
                                text
                                color="primary"
                                @click="dateMenu = false"
                            >
                                Cancel
                            </v-btn>
                            <v-btn
                                text
                                color="primary"
                                @click="selectDate(date)"
                            >
                                OK
                            </v-btn>
                        </v-date-picker>
                    </v-menu>
                    <!-- 'date_day'-->
                    <v-menu
                        v-if="report.param_csv.includes('date_day')"
                        ref="date_picker"
                        v-model="date_picker"
                        :close-on-content-click="false"
                        transition="scale-transition"
                        offset-y
                        min-width="auto"
                    >
                        <template v-slot:activator="{ on, attrs }">
                            <v-text-field
                                v-model="dateDayText"
                                label="Date"
                                append-icon="mdi-calendar"
                                readonly
                                v-bind="attrs"
                                outlined
                                v-on="on"
                            />
                        </template>
                        <v-date-picker
                            v-model="date_day"
                            no-title
                            @input="date_picker = false"
                        />
                    </v-menu>                   
                    <!-- 'order_by'-->
                    <v-select
                        v-if="report.orderby_csv && report.orderby_csv.length > 0"                    
                        v-model="order_by"
                        :items="orderByParam"
                        item-text="label"
                        item-value="value"
                        outlined
                        label="Order By"
                    />
                    <!-- 'format'-->
                    <v-select                     
                        v-model="format"
                        :items="formats"
                        item-text="label"
                        item-value="value"
                        outlined
                        label="Format"
                    />
                </v-form>
            </v-card-text>
            <v-card-text
                v-if="reportGenerateStatus === 'PROCESSING'"
                class="mt-4 pb-8 text-center"
            >
                <v-progress-circular
                    :size="60"
                    color="primary"
                    indeterminate
                />
                <span class="subtitle-2 d-block">Please wait while your report is being generated ...</span>
            </v-card-text>
            <v-card-text
                v-if="reportGenerateStatus === 'PROCESSED'"
                class="mt-4 pb-6 text-center"
            >
                <v-icon
                    large
                    color="success"
                >
                    mdi-checkbox-marked-circle
                </v-icon>
                <span class="subtitle-2 d-block my-2">Completed successfully</span>
                <v-row class="text-left download-file mt-3">
                    <v-col cols="1">
                        <v-icon class="file-icon">
                            {{ reportIcon }}
                        </v-icon>
                    </v-col>
                    <v-col
                        cols="9"
                        class="subtitle-1 font-weight-medium"
                    >
                        {{ reportFileName }}
                    </v-col>
                    <v-col cols="2">
                        <v-btn
                            small
                            class="float-right primary px-0 mt-1"
                            style="text-transform: none !important; height: 32px;"
                            :href="reportLink"
                            target="_blank"
                        >
                            Open
                        </v-btn>
                    </v-col>
                </v-row>
            </v-card-text>
            <v-card-text
                v-if="reportGenerateStatus === 'FAILED'"
                class="mt-6 pb-8 text-center"
            >
                <span class="subtitle-2 d-block red--text">{{ reportGenerateError }}</span>
            </v-card-text>
            <v-card-actions class="px-6 pb-4 pt-0">
                <v-spacer />
                <v-btn
                    v-if="reportGenerateStatus === '' || reportGenerateStatus === 'PROCESSING'"
                    style="text-transform: none !important"
                    class="text-subtitle-2"
                    outlined
                    :width="this.$vuetify.breakpoint.xs ? '140px' : '76px'"
                    @click="closeDialog"
                >
                    Cancel
                </v-btn>
                <v-btn
                    v-else-if="reportGenerateStatus === 'PROCESSED'"
                    style="text-transform: none !important"
                    class="text-subtitle-2"
                    outlined
                    :width="this.$vuetify.breakpoint.xs ? '140px' : '76px'"
                    @click="closeDialog"
                >
                    Close
                </v-btn>
                <v-btn
                    v-else-if="reportGenerateStatus === 'FAILED'"
                    style="text-transform: none !important"
                    class="text-subtitle-2"
                    outlined
                    :width="this.$vuetify.breakpoint.xs ? '140px' : '76px'"
                    @click="reportGenerateStatus = ''"
                >
                    Close
                </v-btn>
                <v-btn
                    v-if="!reportGenerateStatus"
                    style="text-transform: none !important"
                    class="text-subtitle-2"
                    depressed
                    :disabled="!formValid"
                    color="primary"
                    :width="this.$vuetify.breakpoint.xs ? '140px' : '76px'"
                    @click="generateReport"
                >
                    Generate
                </v-btn>
                <v-spacer v-if="this.$vuetify.breakpoint.xs" />
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>
<script>
import webtmsBackend from '@/services/webtmsBackend';
import constants from '@/constants';

import dayjs from 'dayjs';
import 'dayjs/plugin/utc'; // import UTC plugin
import 'dayjs/plugin/advancedFormat'; // import advancedFormat plugin

export default {
    props: {
        value: Boolean,
        report: {
            type: Object,
            required: true,
            default: () => {}
        }
    },
    data: () => ({
        valid: false,
        center: '',
        ticket: '',
        date_field: 'due_date',        
        accounts: [],
        member_codes: [],       
        response_codes: [],
        billing_codes: [],
        locators: [],        
        dateMenu: null,
        date_to: dayjs().endOf('month').format('YYYY-MM-DD'),
        date_from: dayjs().startOf('month').format('YYYY-MM-DD'),        
        date_day: dayjs().startOf('month').format('YYYY-MM-DD'),
        order_by: '',       
        format:'',
        cid: '',        
        formValid: false,
        showAlert: false,
        date_picker: false,
        alertMessage: '',
        reportGenerateStatus: '',
        reportRowId: '',
        reportFileName: '',
        reportGenerateError: '',        
        centers: [],
        dateFields: [
            { title: 'Due Date', value: 'due_date' },
            { title: 'Work Date', value: 'work_date' },
            { title: 'Expires Date', value: 'expires_date' }
        ],
        date: [dayjs().startOf('month').format('YYYY-MM-DD'), dayjs().endOf('month').format('YYYY-MM-DD')],
    }),    
    computed: {
        user() {
            return this.$store.getters.user;
        },
        formats () {
            const reportFormats = this.report.format_csv.split(','); 
            return reportFormats.map(item => {
                return {
                    label: item.toUpperCase(),
                    value: item.toLowerCase()
                };
            });
        },
        params() {
            return [this.report.param_csv];           
        },
        requiredParams() {
            if(this.report.param_csv === '') {
                return [];
            }
            const params = this.report.param_csv.split(',');
            return params;
        },
        orderByParam() {
            const orderBy = this.report.orderby_csv.split(',');
            if(orderBy.length === 0) {
                return [];
            }
            return orderBy.map(item => {
                return {
                    label: item.toUpperCase(),
                    value: item.toLowerCase()
                };
            });      
        },
        show: {
            get() { 
                console.log('computed.show.get', this.value);
                if(this.value === true) {
                    this.resetFieldsToDefault();
                }
                return this.value;
            },
            set(value) {
                console.log('computed.show.set', value);
                console.log('computed.show.set', this.report);
                this.$emit('input', value);
                this.resetFieldsToDefault();
            },
        },
        companyCenters() {
            let centers = this.$store.getters.companyCenters;
            let showCenters = centers.map((item) => {
                return { text: `${item.center_abbrev}`, value: `${item.center_id}` };
            });
            return showCenters;
        },
        companyUsers() {
            // build list of users for this company, including deleted users and excluding SYSOP accounts (if any)
            let companyUsersData = this.$store.getters.companyUsers;
            let allUsers = companyUsersData
                .filter(item => item.ingroup.toUpperCase() !== 'SYSOP')
                .map(item => {
                    return { text: `${item.account} "${item.name}"`, value: item.account };
                });
            allUsers.push({text:'SYSTEM (Automation)', value:'SYSTEM'});
            return allUsers;
        },
        companyMemberCodes() {
            let memberCodes = this.$store.getters.companyMemberCodes;
            return memberCodes.map((item) => {
                return { text: `${item.member_code}${item.enabled ? '':' (disabled)'}`, value: item.member_code };
            });
        },
        companyResponseCodes () {
            let responseCodes = this.$store.getters.companyResponseCodes;
            return responseCodes.map((item) => {
                return { 
                    text: `${item.response_code} - ${item.description}`, 
                    value: item.response_code
                };
            });
        },
        companyBillingCodes () {
            let billingCodes = [...this.$store.getters.companyBilling.codes];
            return billingCodes.map((item) => {
                return { text: `${item.code} - ${item.description}${item.enabled ? '':' (disabled)'}`, value: item.code };
            });
        },
        dateText () {
            return this.date.map(dt => dayjs(dt).format('MM/DD/YYYY')).join(' ~ ');
        },
        reportLink() {
            return `${constants.NEWTIN_DOMAIN || ''}/NewtinWeb/userdata/${this.$store.getters.user.account}$${this.$store.getters.user.uid.slice(1, -1)}/${this.reportFileName}`;
        },
        reportIcon() {
            const ext = this.reportFileName.split('.').pop().toUpperCase();
            if (ext === 'PDF') {
                return 'mdi-file-pdf-box';
            } else if(ext === 'DOC' || ext === 'DOCX') {
                return 'mdi-file-word';
            } else if(ext === 'XLS' || ext === 'XLSX') {
                return 'mdi-file-excel';
            } else if(ext === 'TIFF') {
                return 'mdi-file-image';
            } else {
                return 'mdi-file';
            }
        },
        // Locators
        companyLocators() {
            let overviewAssigned = [...this.$store.getters.overviewOpenTickets.overviewAssigned];
            overviewAssigned.unshift({ account: '',  name: 'un-assigned', uid: '00000000-0000-0000-0000-000000000000' });
            return overviewAssigned.map((item) => {
                item.title = item.account + ' (' + item.name +')'; 
                return item;
            });
        },
        selectedAllLocators () {
            return this.locators.length === this.companyLocators.length;
        },
        selectedSomeLocators () {
            return this.locators.length > 0 && !this.selectedAllLocators;
        },
        selectLocatorsIcon () {
            if (this.selectedAllLocators) return 'mdi-close-box';
            if (this.selectedSomeLocators) return 'mdi-minus-box';
            return 'mdi-checkbox-blank-outline';
        },
        dateDayText () {
            return this.date_day ? dayjs(this.date_day).format('MM/DD/YYYY') : dayjs().format('MM/DD/YYYY');
        },
    },
    watch: {
        format() {
            this.validateForm();
        },
        orderBy() {
            this.validateForm();
        },
        accounts() {
            this.validateForm();
        },
        member_codes() {
            this.validateForm();
        },
        billing_codes() {
            this.validateForm();
        },
        response_codes() {
            this.validateForm();
        },
        date() {
            this.validateForm();    
        },
        locators() {
            this.validateForm();
        },
        ticket() {
            this.validateForm();
        },  
        date_field() {
            this.validateForm();
        },
        date_day() {
            this.validateForm();
        }
    },
    mounted() {
        this.resetFieldsToDefault();
        setTimeout(() => {
            this.setFocusTicketNumber();
        }, 2);

    },
    methods: {
        setFocusTicketNumber() {
            if(this.$refs.ticketNumber) {
                this.$refs.ticketNumber.focus();
            }
        },
        resetFieldsToDefault() {
            this.formValid = false;
            if(this.report) {
                this.accounts = [];
                this.member_codes = [];
                this.response_codes = [];
                this.billing_codes = [];
                this.locators = [];
                this.reportGenerateStatus = '';
                this.reportRowId = '';
                this.reportFileName = '';
                this.center = (this.companyCenters.length == 1) ? this.companyCenters[0].value : '';
                this.ticket = '';
                this.date_field = 'due_date';
                this.date_picker = false;
                this.date_day = dayjs().startOf('month').format('YYYY-MM-DD');
                if(this.report.name.startsWith('TMS/')) {
                    this.date = [dayjs().startOf('month').format('YYYY-MM-DD'), dayjs().add(1, 'months').startOf('month').format('YYYY-MM-DD')];
                } else {
                    this.date = [dayjs().startOf('month').format('YYYY-MM-DD'), dayjs().endOf('month').format('YYYY-MM-DD')];
                }
                this.setOrderBy();
                this.setFormat();           

            }           
        },
        setOrderBy() {
            const orderBy = this.report.orderby_csv.split(',');
            if (Array.isArray(orderBy) && orderBy.length > 0) {
                this.order_by = orderBy[0];
            }           
        },
        setFormat() {
            const reportFormats = this.report.format_csv.split(','); 
            if (Array.isArray(reportFormats) && reportFormats.length > 0) {
                this.format = reportFormats[0].toLowerCase();
            }
        },
        validateForm() {
            this.formValid = this.requiredParams !== '' ? this.requiredParams.every(param => param && param.length > 0) : true;                   
        },
        selectDate(value) {
            if (value.length !== 2) {
                this.showAlert = true;
                this.alertMessage = 'Please select date range';
                return;
            }
            this.$refs.dateMenu.save(value);
        },
        async generateReport() {
            
            let params = this.report.param_csv.split(',');
            let paramsRequired = this.report.param_required_csv.split(',');
            let param = {};

            // Validate Required parameters
            // Can be any of: cid,center,date_from,date_to,member_codes,response_codes,accounts,locators,ticket,date_field,date_day,billing_codes

            this.cid = this.user.cid;

            if(params.includes('center')) {
                if(paramsRequired.includes('center') && (!this.center || this.center.length === 0)) {
                    this.showAlert = true;
                    this.alertMessage = 'Please select 811 Center to generate this report';
                    return;
                }
                param['center'] = this.center;
            }   

            // date_from, date_to
            if(params.includes('date_from')) {
                const dateRange = this.dateText.split(' ~ ');
                this.date_from = dateRange[0];
                this.date_to = dateRange[1];
                if(paramsRequired.includes('date_from') && (!this.date_from || !this.date_to)) {
                    this.showAlert = true;
                    this.alertMessage = 'Date From and Date To are required to generate this report';
                    return;
                }
                param['date_from'] = this.date_from;
                param['date_to'] = this.date_to;
            }

            // member_codes
            if(params.includes('member_codes')) {
                param['member_codes'] = this.member_codes.map(item => item.value).join(',');
            }

            // response_codes
            if(params.includes('response_codes')) {
                param['response_codes'] = this.response_codes.map(item => item.value).join(',');
            }
            // accounts
            if(params.includes('accounts')) {
                param['accounts'] = this.accounts.map(item => item.value).join(',');
            }
            // locators
            if(params.includes('locators')) {
                param['locators'] = this.locators.map(item => item.value).join(',');
            }
            // billing_codes
            if(params.includes('billing_codes')) {
                param['billing_codes'] = this.billing_codes.map(item => item.value).join(',');
            }
            // ticket
            if(params.includes('ticket')) {
                if(paramsRequired.includes('ticket') && !this.ticket) {
                    this.showAlert = true;
                    this.alertMessage = 'Please enter Ticket Number';
                    return;
                }
                param['ticket'] = this.ticket;
            }
            // date_field
            if(params.includes('date_field')) {
                if(paramsRequired.includes('date_field') && !this.date_field) {
                    this.showAlert = true;
                    this.alertMessage = 'Please select Date Field to generate this report';
                    return;
                }
                param['date_field'] = this.date_field;
            }
            // date_day
            if(params.includes('date_day')) {
                if(paramsRequired.includes('date_day') && !this.date_day) {
                    this.showAlert = true;
                    this.alertMessage = 'Please select Date to generate this report';
                    return;
                }
                param['date_day'] = this.date_day;
            }
            // order_by
            if(this.report.orderby_csv.length > 0) {
                if(this.order_by.length === 0) {
                    this.showAlert = true;
                    this.alertMessage = 'Please select Order By to generate this report';
                    return;
                }
                param['order_by'] = this.order_by;
            }
            
            // format
            param.format= this.format;
            if(param.format.length === 0) {
                this.showAlert = true;
                this.alertMessage = 'Please select Format to generate this report';
                return;
            }

            this.reportGenerateStatus = 'PROCESSING';
            const response = await webtmsBackend.generateReportTMS(param, this.report.name);
            if (response.task && response.task.rowid) {
                this.reportRowId = response.task.rowid;
                this.checkReportStatus();
            } else {
                this.reportGenerateError = response.err || 'Failed to generate report';
                this.reportGenerateStatus = 'FAILED';
            }
        },
        async checkReportStatus() {
            const response = await webtmsBackend.checkReportStatus(this.reportRowId, this.report.name);
            if (response.task && response.task.processed === true) {
                if (response.task.success === true) {
                    this.reportGenerateStatus = 'PROCESSED';
                    this.reportFileName = response.task.task_output_ref;
                } else {
                    this.reportGenerateStatus = 'FAILED';
                    this.reportGenerateError = response.task.task_running_status;
                }
            }
            // only if we got back a valid task and !processed, check again in 2 seconds
            if (response.task && response.task.processed === false) {
                setTimeout(() => {
                    this.checkReportStatus();
                }, 2000);
            }
        },
        addCustomAccount(event) {
            const input = event.target.value;
            if (input && !this.accounts.includes(input)) {
                this.accounts.push(input);
            }
        },
        closeDialog() {
            this.show = false;
            this.resetFieldsToDefault();            
        }
    }
};
</script>
<style scoped>
    .download-file {
    background-color: #F5F7FA;
    min-height: 55px;
    padding: 0 5px;
    }
    .file-icon {
    font-size: 30px;
    }
</style>
